export interface InterviewDisconnectInfo {
  code: number;
  willReconnect: boolean;
}

export interface CounterpartPresentationPayload {
  displayName: string;
  role: string;
  image?: string;
  videoFile?: string;
}

export interface InterviewConnectionLifecycleDependencies {
  connect: () => Promise<void>;
  createSessionId: () => string;
  buildConnectPayload: (sessionId: string) => Record<string, unknown>;
  sendConnectSession: (payload: Record<string, unknown>) => void;
  registerDisconnect: (callback: (info: InterviewDisconnectInfo) => void) => void;
  registerReconnect: (callback: () => void) => void;
  getSessionId: () => string | null;
  setSessionId: (sessionId: string | null) => void;
  setSessionReady: (ready: boolean) => void;
  setSttReady: (ready: boolean) => void;
  setTargetCategories: (categories: string[]) => void;
  setCounterpartPresentation: (presentation: CounterpartPresentationPayload | null) => void;
  isRecording: () => boolean;
  canRestartRecording: () => boolean;
  prepareSttForReconnect: (wasRecording: boolean) => void | Promise<void>;
  resetDisconnectedUi: () => void;
  toast: (opts: {
    title: string;
    description: string;
    variant?: "default" | "destructive" | null | undefined;
  }) => void;
  logInitialConnection: (sessionId: string) => void;
  logDisconnect: (info: InterviewDisconnectInfo) => void;
  logReconnect: (sessionId: string, shouldAutoRestartRecording: boolean) => void;
  onConnectionError: (error: unknown) => void;
}

/**
 * 面接WebSocketの初回接続・切断・再接続だけを管理する。
 * STTの実際の再開はconnect_session確定後に別コントローラが行う。
 */
export class InterviewConnectionLifecycleController {
  private connectPayload: Record<string, unknown> | null = null;
  private disconnectNotified = false;
  private finalReconnectFailureNotified = false;
  private wasRecordingBeforeDisconnect = false;

  constructor(private readonly dependencies: InterviewConnectionLifecycleDependencies) {}

  async start(): Promise<boolean> {
    this.dependencies.setSessionReady(false);

    try {
      await this.dependencies.connect();
    } catch (error) {
      this.dependencies.onConnectionError(error);
      return false;
    }

    const sessionId = this.dependencies.createSessionId();
    this.dependencies.setSessionId(sessionId);
    this.connectPayload = this.dependencies.buildConnectPayload(sessionId);
    this.dependencies.sendConnectSession(this.connectPayload);
    this.dependencies.logInitialConnection(sessionId);

    // 現行順序を維持し、初回接続が成功した後に切断通知を登録する。
    this.dependencies.registerDisconnect((info) => this.handleDisconnect(info));
    this.dependencies.registerReconnect(() => this.handleReconnect());
    return true;
  }

  handleConnected(payload: Record<string, unknown> = {}): void {
    this.dependencies.setSessionReady(true);
    const { targetCategories, counterpartPresentation } = payload;
    if (targetCategories) {
      this.dependencies.setTargetCategories(targetCategories as string[]);
    }
    this.dependencies.setCounterpartPresentation(
      isCounterpartPresentation(counterpartPresentation) ? counterpartPresentation : null,
    );
  }

  handleDisconnect(info: InterviewDisconnectInfo): void {
    this.dependencies.setSessionReady(false);
    this.dependencies.logDisconnect(info);

    const wasRecording = this.dependencies.isRecording();
    this.wasRecordingBeforeDisconnect = wasRecording && info.willReconnect;
    this.dependencies.setSttReady(false);
    void this.dependencies.prepareSttForReconnect(wasRecording);
    this.dependencies.resetDisconnectedUi();

    const canAutoRestartRecording = this.wasRecordingBeforeDisconnect
      && this.dependencies.canRestartRecording();

    if (info.willReconnect) {
      if (this.disconnectNotified) return;

      this.disconnectNotified = true;
      this.dependencies.toast({
        title: '通信が一時的に切れました',
        description: canAutoRestartRecording
          ? '再接続しています。つながったら録音を自動で再開します。そのままお待ちください。'
          : (wasRecording
            ? '再接続しています。録音はリセットしました。つながったら、もう一度マイクを押して話してください。'
            : '再接続しています。応答が止まった場合は、もう一度マイクを押して話してください。'),
      });
      return;
    }

    if (this.finalReconnectFailureNotified) return;

    this.finalReconnectFailureNotified = true;
    this.dependencies.toast({
      title: '通信を復旧できませんでした',
      description: 'お手数ですが、ページを再読み込みしてからもう一度お試しください。',
      variant: 'destructive',
    });
  }

  handleReconnect(): void {
    this.disconnectNotified = false;
    this.finalReconnectFailureNotified = false;

    const sessionId = this.dependencies.getSessionId();
    if (!sessionId || !this.connectPayload) return;

    const shouldAutoRestartRecording = this.wasRecordingBeforeDisconnect
      && this.dependencies.canRestartRecording();
    this.wasRecordingBeforeDisconnect = false;

    this.dependencies.sendConnectSession({
      ...this.connectPayload,
      sessionId,
      wasRecording: shouldAutoRestartRecording,
    });
    this.dependencies.logReconnect(sessionId, shouldAutoRestartRecording);
  }
}

function isCounterpartPresentation(value: unknown): value is CounterpartPresentationPayload {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  return typeof record.displayName === 'string'
    && record.displayName.trim().length > 0
    && typeof record.role === 'string'
    && record.role.trim().length > 0;
}
